import { useCallback, useMemo, useSyncExternalStore } from "react";
import * as THREE from "three";
import { makePreviewLights } from "../../render/previewLights.js";
import {
  buildPart,
  buildWeapon,
  type BuiltPart,
  type BuiltWeapon,
} from "../../render/weaponBuilder.js";

/**
 * The picture on a gun's tile, and on every part's: the thing itself,
 * rendered. Painted atlas art is right for a tin of beans; a carbine with a
 * scope and a can bolted on is THEIR carbine, so the tile shows the same
 * `buildWeapon(defId, mods)` model as the Werkbank and the viewmodel, and a
 * part's tile is `buildPart` — literally what bolts on.
 *
 * ONE RENDERER, MANY TILES: a browser hands out ~16 WebGL contexts and kills
 * the oldest, so a single offscreen renderer draws each key once, `toDataURL()`s
 * it, and every tile is an `<image href="data:...">`. Rendering is synchronous
 * (a dozen boxes into 512×256, once per key) but never in React's render phase
 * — it hangs off `subscribe`; a tile whose portrait is not ready draws its
 * label and no artwork.
 *
 * Orientation: the muzzle points RIGHT, camera in from +X, nudged a little
 * above and around so the flat-shaded faces do not vanish. The frame is the
 * def's grid footprint, so `artworkViewport` rotates it like an atlas sprite;
 * a part's host-local position is free because the frame is its own bounds.
 */

/** Which builder draws the model, and half of the cache key. */
export type PortraitKind = "weapon" | "attachment";

/** Backing-buffer resolution per grid cell. A 4×2 gun renders at 512×256. */
const PX_PER_CELL = 128;

/** Empty margin around the model, as a fraction of the binding dimension. */
const FRUSTUM_PAD = 0.06;

/**
 * How long the renderer is kept alive after the last portrait. The data URLs
 * are forever; the GL context is one of the browser's handful.
 */
const IDLE_RELEASE_MS = 4000;

/** Rendered portraits, by `itemPortraitKey`. Never evicted: a data URL for a
 *  gun the player owns is a few dozen KB and they will look at it again. */
const cache = new Map<string, string>();

/**
 * Keys `weaponBuilder` has no model for, latched on the first attempt so the
 * request is not reissued on every commit.
 */
const undrawable = new Set<string>();

/** Every mounted tile, so a finished render can repaint the ones waiting. */
const listeners = new Set<() => void>();

let renderer: THREE.WebGLRenderer | null = null;
let scene: THREE.Scene | null = null;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;

/** Latched the first time GL refuses; never retried. */
let unavailable = false;

/**
 * Def id plus fitted parts in slot order — the identity of a silhouette. The
 * KIND leads: it says which builder drew the picture.
 */
export function itemPortraitKey(
  kind: PortraitKind,
  defId: string,
  mods: readonly string[],
): string {
  return `${kind}:${defId}|${mods.join(",")}`;
}

/** The rendered portrait, or `null`. Never renders — safe in a render phase. */
export function getItemPortrait(
  kind: PortraitKind,
  defId: string,
  mods: readonly string[],
): string | null {
  return cache.get(itemPortraitKey(kind, defId, mods)) ?? null;
}

/** Repaint me when a portrait lands. Returns its own removal. */
export function subscribeItemPortraits(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Draws `defId` at the grid footprint unless already drawn or impossible.
 * Synchronous; a no-op outside a browser, after GL refused, or for an
 * undrawable part.
 */
export function requestItemPortrait(
  kind: PortraitKind,
  defId: string,
  mods: readonly string[],
  widthCells: number,
  heightCells: number,
): void {
  const key = itemPortraitKey(kind, defId, mods);
  if (cache.has(key) || undrawable.has(key) || unavailable) return;
  if (typeof document === "undefined") return;

  const gl = acquireRenderer();
  if (gl === null) return;
  const stage = portraitScene();

  let built: BuiltWeapon | BuiltPart | null = null;
  try {
    if (kind === "weapon") {
      // `"missing"` view: every weapon caller derives `mods` from a real
      // instance
      // (the shelf's offers wear every factory part), so an empty slot is
      // genuinely empty.
      built = buildWeapon(defId, mods, { bare: "missing" });
    } else {
      // A part carries no parts of its own, so `mods` is not consulted — it
      // stays
      // in the key so one signature serves both kinds.
      built = buildPart(defId);
      if (built === null) {
        undrawable.add(key);
        return;
      }
    }
    stage.add(built.group);
    stage.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(built.group);
    const center = box.getCenter(new THREE.Vector3());

    // Far enough out that the whole model is in front of the camera; an
    // orthographic projection does not care how far beyond that it sits.
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    const dir = new THREE.Vector3(1, 0.22, 0.12).normalize();
    camera.position.copy(center).addScaledVector(dir, 3);
    camera.up.set(0, 1, 0);
    camera.lookAt(center);
    camera.updateMatrixWorld(true);

    // The bounding box in CAMERA space: an ortho frustum is camera-aligned.
    // Depth runs down −z.
    const corner = new THREE.Vector3();
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < 8; i++) {
      corner.set(
        (i & 1) === 0 ? box.min.x : box.max.x,
        (i & 2) === 0 ? box.min.y : box.max.y,
        (i & 4) === 0 ? box.min.z : box.max.z,
      );
      corner.applyMatrix4(camera.matrixWorldInverse);
      minX = Math.min(minX, corner.x);
      maxX = Math.max(maxX, corner.x);
      minY = Math.min(minY, corner.y);
      maxY = Math.max(maxY, corner.y);
      minZ = Math.min(minZ, corner.z);
      maxZ = Math.max(maxZ, corner.z);
    }

    const frustum = portraitFrustum({ minX, maxX, minY, maxY }, widthCells / heightCells, FRUSTUM_PAD);
    camera.left = frustum.left;
    camera.right = frustum.right;
    camera.top = frustum.top;
    camera.bottom = frustum.bottom;
    camera.near = -maxZ - 0.1;
    camera.far = -minZ + 0.1;
    camera.updateProjectionMatrix();

    gl.setSize(widthCells * PX_PER_CELL, heightCells * PX_PER_CELL, false);
    gl.render(stage, camera);
    cache.set(key, gl.domElement.toDataURL("image/png"));
  } catch (err) {
    // A broken context or unreadable canvas: give up on 3D item art rather than
    // throw out of a React commit.
    console.warn("itemPortrait: no rendered item art this session", err);
    unavailable = true;
    releaseRenderer();
    return;
  } finally {
    if (built !== null) {
      stage.remove(built.group);
      built.dispose();
    }
  }

  armIdleRelease();
  for (const listener of [...listeners]) listener();
}

/**
 * The portrait for `defId` + `mods`, requested on mount and repainted when it
 * arrives. `kind === null` for atlas tiles — hooks cannot be conditional, so
 * the hook subscribes to nothing.
 */
export function useItemPortrait(
  kind: PortraitKind | null,
  defId: string,
  mods: readonly string[],
  widthCells: number,
  heightCells: number,
): string | null {
  // The caller derives `mods` fresh on every render (`fittedMods(...).map(...)`),
  // so identity is worthless and the JOINED list is the dependency.
  const modsKey = mods.join(",");
  const stableMods = useMemo(() => mods.slice(), [modsKey]);

  const subscribe = useCallback(
    (listener: () => void): (() => void) => {
      if (kind === null) return () => {};
      // Listener first: the render below is synchronous and notifies as it
      // finishes, and this tile is the one waiting for it.
      const unsubscribe = subscribeItemPortraits(listener);
      requestItemPortrait(kind, defId, stableMods, widthCells, heightCells);
      return unsubscribe;
    },
    [kind, defId, stableMods, widthCells, heightCells],
  );

  const getSnapshot = useCallback(
    (): string | null => (kind === null ? null : getItemPortrait(kind, defId, stableMods)),
    [kind, defId, stableMods],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Server-rendered markup has no GL and no commit phase, so it has no picture. */
function getServerSnapshot(): string | null {
  return null;
}

/**
 * An orthographic frustum containing `bounds` at `aspect`, centred: the
 * proportionally larger dimension binds, the other grows to the tile's shape.
 * Pure; exported for GPU-less tests.
 */
export function portraitFrustum(
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  aspect: number,
  pad: number = FRUSTUM_PAD,
): { left: number; right: number; top: number; bottom: number } {
  const contentW = bounds.maxX - bounds.minX;
  const contentH = bounds.maxY - bounds.minY;
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  let frustumW: number;
  let frustumH: number;
  if (contentW / contentH > aspect) {
    frustumW = contentW * (1 + pad);
    frustumH = frustumW / aspect;
  } else {
    frustumH = contentH * (1 + pad);
    frustumW = frustumH * aspect;
  }

  return {
    left: centerX - frustumW / 2,
    right: centerX + frustumW / 2,
    top: centerY + frustumH / 2,
    bottom: centerY - frustumH / 2,
  };
}

/** The lit, empty stage. Outlives any one renderer: lights own no GL state. */
function portraitScene(): THREE.Scene {
  if (scene !== null) return scene;
  const made = new THREE.Scene();
  made.name = "itemPortrait";
  for (const light of makePreviewLights()) made.add(light);
  scene = made;
  return made;
}

/**
 * The shared renderer, created on demand with a fresh canvas each time (a
 * `forceContextLoss()`ed element never gives a context again — see
 * `modPreview.ts`).
 */
function acquireRenderer(): THREE.WebGLRenderer | null {
  if (renderer !== null) return renderer;
  try {
    const gl = new THREE.WebGLRenderer({
      canvas: document.createElement("canvas"),
      antialias: true,
      alpha: true,
      // `toDataURL` reads the back buffer, which the browser is otherwise free
      // to have thrown away by the time we ask.
      preserveDrawingBuffer: true,
    });
    gl.setPixelRatio(1);
    gl.setClearColor(0x000000, 0);
    renderer = gl;
    return gl;
  } catch (err) {
    console.warn("itemPortrait: no WebGL context for item art", err);
    unavailable = true;
    return null;
  }
}

function armIdleRelease(): void {
  if (releaseTimer !== null) clearTimeout(releaseTimer);
  releaseTimer = setTimeout(releaseRenderer, IDLE_RELEASE_MS);
}

function releaseRenderer(): void {
  if (releaseTimer !== null) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
  const gl = renderer;
  renderer = null;
  if (gl === null) return;
  gl.dispose();
  gl.forceContextLoss();
}
